// src/pages/TextesReglementaires/PolitiqueConfidentialitePage.jsx

import React, { useEffect, useMemo, useState } from "react";

import useScrollToTop from "../../hooks/useScollToTop";
import { getSettings } from "../../services/settingsServices";

import LegalPageLayout from "./components/LegalPageLayout";
import {
  formatLegalDate,
  getMairieIdentity,
  getSettingsRecord,
} from "./utils/legalPagesUtils";

export default function PolitiqueConfidentialitePage() {
  useScrollToTop();

  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    (async () => {
      setLoading(true);

      try {
        const data = await getSettings();

        if (mounted) {
          setSettings(getSettingsRecord(data));
        }
      } catch (error) {
        console.error(
          "Erreur chargement settings politique confidentialité:",
          error
        );

        if (mounted) {
          setSettings({});
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);

  const identity = useMemo(() => getMairieIdentity(settings), [settings]);
  const updatedAt = formatLegalDate(settings.updated_at);

  if (loading) {
    return (
      <div className="mx-auto max-w-[1240px] px-4 pb-16 pt-32 sm:px-6">
        <div className="animate-pulse space-y-5">
          <div className="h-8 w-72 rounded-full bg-slate-200 dark:bg-slate-800" />
          <div className="h-5 w-full max-w-2xl rounded-full bg-slate-200 dark:bg-slate-800" />
          <div className="h-96 rounded-[2rem] bg-slate-200 dark:bg-slate-800" />
        </div>
      </div>
    );
  }

  return (
    <LegalPageLayout
      title="Politique de confidentialité"
      eyebrow="Textes réglementaires"
      description="Informations relatives à la collecte, à l’utilisation, à la conservation et à la protection des données personnelles sur le site internet officiel de la mairie."
      identity={identity}
      updatedAt={updatedAt}
    >
      <h2>1. Objet de la politique de confidentialité</h2>

      <p>
        La présente politique de confidentialité a pour objectif d’informer les
        usagers du site internet <strong>{identity.siteUrl}</strong> sur la
        manière dont <strong>{identity.name}</strong> collecte, utilise,
        conserve et protège les données personnelles susceptibles d’être
        traitées dans le cadre de la consultation du site et des services
        numériques proposés.
      </p>

      <p>
        La mairie attache une importance particulière à la protection des données
        personnelles des administrés, visiteurs, partenaires, associations,
        commerçants et utilisateurs du site.
      </p>

      <h2>2. Responsable du traitement</h2>

      <p>
        Le responsable du traitement des données personnelles collectées sur le
        site est <strong>{identity.name}</strong>, représentée par son maire ou
        par toute personne dûment habilitée.
      </p>

      <ul>
        <li>
          <strong>Collectivité :</strong> {identity.name}
        </li>

        {identity.address && (
          <li>
            <strong>Adresse :</strong>{" "}
            <span className="whitespace-pre-line">{identity.address}</span>
          </li>
        )}

        {identity.email && (
          <li>
            <strong>Contact :</strong>{" "}
            <a href={`mailto:${identity.email}`}>{identity.email}</a>
          </li>
        )}

        {identity.phone && (
          <li>
            <strong>Téléphone :</strong>{" "}
            <a href={`tel:${String(identity.phone).replace(/\s+/g, "")}`}>
              {identity.phone}
            </a>
          </li>
        )}
      </ul>

      <h2>3. Données personnelles susceptibles d’être collectées</h2>

      <p>
        Lors de l’utilisation du site, différentes catégories de données peuvent
        être traitées selon les services utilisés.
      </p>

      <p>Il peut notamment s’agir des données suivantes :</p>

      <ul>
        <li>nom et prénom ;</li>
        <li>adresse e-mail ;</li>
        <li>numéro de téléphone ;</li>
        <li>adresse postale ;</li>
        <li>informations transmises via un formulaire de contact ;</li>
        <li>informations liées à une demande administrative ou municipale ;</li>
        <li>données techniques de navigation strictement nécessaires ;</li>
        <li>adresse IP, journaux techniques ou informations de sécurité ;</li>
        <li>
          informations relatives aux préférences de cookies lorsque le site
          utilise un bandeau de consentement.
        </li>
      </ul>

      <p>
        Les données demandées dans les formulaires sont limitées aux informations
        nécessaires au traitement de la demande de l’usager.
      </p>

      <h2>4. Finalités des traitements</h2>

      <p>
        Les données personnelles collectées par le site peuvent être utilisées
        pour les finalités suivantes :
      </p>

      <ul>
        <li>répondre aux demandes envoyées via les formulaires de contact ;</li>
        <li>orienter les usagers vers les services municipaux compétents ;</li>
        <li>assurer la gestion des demandes administratives en ligne ;</li>
        <li>gérer les inscriptions éventuelles à des événements ou services ;</li>
        <li>diffuser des informations municipales lorsque l’usager l’a demandé ;</li>
        <li>sécuriser le site et prévenir les usages frauduleux ;</li>
        <li>mesurer la fréquentation du site lorsque l’outil utilisé le permet ;</li>
        <li>améliorer les contenus, l’ergonomie et les services proposés.</li>
      </ul>

      <p>
        Les données ne sont pas utilisées à des fins commerciales étrangères aux
        missions de la mairie.
      </p>

      <h2>5. Bases légales des traitements</h2>

      <p>
        Selon les cas, les traitements de données personnelles mis en œuvre par
        la mairie peuvent reposer sur l’une des bases légales suivantes :
      </p>

      <ul>
        <li>
          <strong>l’exécution d’une mission d’intérêt public</strong>, lorsque le
          traitement est nécessaire à l’exercice des missions de la collectivité ;
        </li>
        <li>
          <strong>le respect d’une obligation légale</strong>, lorsque la mairie
          doit traiter certaines informations en application d’un texte ;
        </li>
        <li>
          <strong>le consentement de l’usager</strong>, notamment pour certains
          formulaires, abonnements ou traceurs non essentiels ;
        </li>
        <li>
          <strong>l’intérêt légitime</strong>, notamment pour assurer la sécurité
          technique du site, lorsque cette base est applicable.
        </li>
      </ul>

      <h2>6. Destinataires des données</h2>

      <p>
        Les données personnelles collectées sont destinées aux services
        municipaux compétents de <strong>{identity.name}</strong>, dans la limite
        de leurs attributions et uniquement lorsque cela est nécessaire au
        traitement de la demande.
      </p>

      <p>
        Certaines données peuvent également être accessibles à des prestataires
        techniques intervenant pour le compte de la mairie, notamment pour
        l’hébergement, la maintenance, la sécurité ou le fonctionnement du site.
        Ces prestataires ne peuvent agir que sur instruction de la mairie.
      </p>

      <p>
        Les données ne sont pas vendues à des tiers.
      </p>

      <h2>7. Durée de conservation</h2>

      <p>
        Les données personnelles sont conservées pendant une durée limitée,
        adaptée à la finalité du traitement et aux obligations légales ou
        réglementaires applicables.
      </p>

      <p>À titre indicatif :</p>

      <ul>
        <li>
          les demandes envoyées via un formulaire peuvent être conservées le
          temps nécessaire à leur traitement ;
        </li>
        <li>
          les échanges administratifs peuvent être conservés selon les règles
          applicables aux archives publiques ;
        </li>
        <li>
          les journaux techniques peuvent être conservés pour une durée limitée à
          des fins de sécurité ;
        </li>
        <li>
          les préférences de cookies peuvent être conservées pendant la durée
          prévue par la réglementation applicable.
        </li>
      </ul>

      <h2>8. Droits des personnes concernées</h2>

      <p>
        Conformément à la réglementation applicable en matière de protection des
        données personnelles, les usagers disposent, selon les cas, des droits
        suivants :
      </p>

      <ul>
        <li>droit d’accès aux données les concernant ;</li>
        <li>droit de rectification des données inexactes ou incomplètes ;</li>
        <li>droit d’opposition au traitement dans certains cas ;</li>
        <li>droit à l’effacement lorsque la réglementation le permet ;</li>
        <li>droit à la limitation du traitement ;</li>
        <li>droit au retrait du consentement lorsque le traitement repose sur le consentement ;</li>
        <li>droit d’introduire une réclamation auprès de la CNIL.</li>
      </ul>

      <p>
        Pour exercer ces droits, l’usager peut contacter la mairie ou le délégué
        à la protection des données.
      </p>

      {identity.dpoEmail ? (
        <p>
          Contact DPO / RGPD :{" "}
          <a href={`mailto:${identity.dpoEmail}`}>{identity.dpoEmail}</a>
        </p>
      ) : identity.email ? (
        <p>
          Contact mairie :{" "}
          <a href={`mailto:${identity.email}`}>{identity.email}</a>
        </p>
      ) : null}

      <h2>9. Délégué à la protection des données</h2>

      <p>
        La mairie peut s’appuyer sur un délégué à la protection des données
        chargé d’accompagner la collectivité dans sa conformité au RGPD, de
        conseiller les services municipaux et de répondre aux demandes relatives
        aux données personnelles.
      </p>

      <p>
        Le DPO peut être interne à la collectivité, mutualisé avec d’autres
        collectivités ou désigné auprès d’un organisme accompagnant la commune.
      </p>

      <h2>10. Sécurité des données</h2>

      <p>
        La mairie met en œuvre des mesures techniques et organisationnelles
        destinées à protéger les données personnelles contre l’accès non
        autorisé, la perte, l’altération, la divulgation ou la destruction.
      </p>

      <p>
        Ces mesures peuvent notamment comprendre la sécurisation des accès, la
        limitation des droits utilisateurs, la maintenance des outils, la
        sauvegarde des données et la sensibilisation des personnes habilitées à
        traiter les informations.
      </p>

      <h2>11. Cookies et traceurs</h2>

      <p>
        Le site peut utiliser des cookies ou traceurs strictement nécessaires à
        son fonctionnement. Ces cookies permettent par exemple d’assurer la
        sécurité, la navigation ou la mémorisation de certains choix techniques.
      </p>

      <p>
        Le site peut également utiliser, selon les outils activés par la mairie,
        des cookies ou traceurs de mesure d’audience, d’intégration de contenus
        externes, de cartographie, de vidéos ou de services tiers.
      </p>

      <p>
        Lorsque le consentement est requis, l’utilisateur doit pouvoir accepter,
        refuser ou modifier ses choix à tout moment selon les modalités prévues
        sur le site.
      </p>

      <h2>12. Services tiers intégrés au site</h2>

      <p>
        Certaines pages peuvent intégrer des services tiers, par exemple des
        cartes interactives, vidéos, formulaires externes, plateformes
        administratives ou contenus issus de réseaux sociaux.
      </p>

      <p>
        Ces services peuvent déposer leurs propres cookies ou traiter certaines
        données selon leurs propres politiques de confidentialité. L’utilisateur
        est invité à consulter les informations fournies par ces services tiers.
      </p>

      <h2>13. Transfert des données hors Union européenne</h2>

      <p>
        La mairie privilégie, lorsque cela est possible, des outils et
        prestataires permettant un hébergement ou un traitement des données dans
        l’Union européenne.
      </p>

      <p>
        Si certains services tiers impliquent un transfert de données hors Union
        européenne, la mairie veille à ce que des garanties appropriées soient
        mises en place conformément à la réglementation applicable.
      </p>

      <h2>14. Mineurs</h2>

      <p>
        Certains services municipaux peuvent concerner des mineurs, notamment
        dans les domaines scolaire, périscolaire, sportif, culturel ou
        associatif.
      </p>

      <p>
        Lorsque des données concernant des mineurs sont collectées, la mairie
        veille à limiter les informations demandées à ce qui est strictement
        nécessaire et à respecter les règles applicables en matière d’information
        des représentants légaux.
      </p>

      <h2>15. Mise à jour de la politique de confidentialité</h2>

      <p>
        La présente politique de confidentialité peut être modifiée à tout moment
        afin de tenir compte des évolutions du site, des services proposés, des
        outils utilisés ou du cadre réglementaire applicable.
      </p>

      <p>
        La version applicable est celle publiée sur le site au moment de la
        consultation.
      </p>

      <h2>16. Contact</h2>

      <p>
        Pour toute question relative à la protection des données personnelles ou
        à la présente politique de confidentialité, vous pouvez contacter{" "}
        <strong>{identity.name}</strong>.
      </p>

      {identity.email && (
        <p>
          E-mail mairie :{" "}
          <a href={`mailto:${identity.email}`}>{identity.email}</a>
        </p>
      )}

      {identity.dpoEmail && identity.dpoEmail !== identity.email && (
        <p>
          E-mail DPO / RGPD :{" "}
          <a href={`mailto:${identity.dpoEmail}`}>{identity.dpoEmail}</a>
        </p>
      )}

      {identity.address && (
        <p>
          Adresse :{" "}
          <strong className="whitespace-pre-line">{identity.address}</strong>
        </p>
      )}
    </LegalPageLayout>
  );
}